/**
 * ScoreBar — Colored progress bar for metrics/scores
 * Used across: Confidence, Compliance %, Success Rate, Relevance
 */

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ScoreBarProps {
  value: number;  // 0-100 or 0-1
  max?: number;   // default 100
  label?: string;
  showValue?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function getColor(pct: number): string {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 60) return 'bg-amber-500';
  if (pct >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

export function ScoreBar({ value, max = 100, label, showValue = true, size = 'md', className }: ScoreBarProps) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const heights = { sm: 'h-1', md: 'h-1.5', lg: 'h-2' };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('flex items-center gap-2', className)}>
            <div className={cn('flex-1 rounded-full bg-muted overflow-hidden', heights[size])}>
              <div
                className={cn('rounded-full transition-all duration-500', heights[size], getColor(pct))}
                style={{ width: `${pct}%` }}
              />
            </div>
            {showValue && (
              <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">
                {max === 1 ? `${(value * 100).toFixed(0)}%` : `${Math.round(value)}%`}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label ?? 'Score'}: {max === 1 ? (value * 100).toFixed(1) : value.toFixed(1)}%</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
